import { useEffect, useRef, useState } from 'react';
import { api, darf, zeitpunkt, type Anmelder } from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { MitGrund } from './Aufmass.tsx';
import Abwurf from '../teile/Abwurf.tsx';
import Kameraknopf from '../teile/Kameraknopf.tsx';
import { ausAbwurf, type Baumdatei } from '../teile/ordnerabwurf.ts';
import { verkleinern } from '../teile/bildverkleinern.ts';
import { bytes } from '../teile/dateiart.ts';
import { Dateikachel } from '../teile/Dateikachel.tsx';
import { useDateifenster } from '../teile/Dateifenster.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

/**
 * Die Dateien eines Auftrags. EIN Reiter, nicht zwei.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „außerdem hötte ich gerne eine ansprechendere sicht mit drag & drop. so
 *    ist das zu statisch. auch brauche ich ja nicht Dateien und
 *    Dokumentation. einmal reicht mir die ordnerstruktur zum auftrag mit den
 *    dateien. der innendienst muss auch nicht wissen das sie pflicht sind zum
 *    hochladen, denn wir laden sie ja nicht hoch. die gewerke müssen sie
 *    füllen."
 *
 * ES WAREN ZWEI BILDSCHIRME AUF DIESELBE FRAGE. „Dokumentation" war die
 * Ordneransicht des Monteurs mit Vorschaubildern und einem Knopf je Ordner;
 * „Dateien" war die Tabellenansicht des Innendienstes. Beide zeigten
 * dieselben `datei_bezug`-Zeilen derselben Ordner desselben Auftrags. Wer
 * eine Datei suchte, musste raten, in welchem der beiden sie steht.
 *
 * JETZT EINER, UND ER ZEIGT FÜR JEDEN DAS RICHTIGE:
 *   - dieselben Ordnerkarten mit Vorschaubildern, für alle
 *   - Abwurf AUF die Karte, mit Ordnern darin
 *   - der Sub sieht die Ordner SEINES Gewerks, der Innendienst alle
 *   - die Dateiliste mit Art, „intern", ändern und weg nur für den Innendienst
 *
 * UND „PFLICHT" STEHT NUR DA, WO JEMAND ETWAS HOCHLADEN MUSS. Marvin hat
 * recht: der Innendienst lädt diese Nachweise nicht hoch, die Gewerke tun
 * das. Ein rotes „Pflicht" an fünfzehn Ordnern, für das der Leser nichts tun
 * kann, ist keine Auskunft, sondern Lärm - und Lärm, den man täglich sieht,
 * macht blind für den Merker, der wirklich zählt. Der Innendienst bekommt
 * stattdessen EINE Zeile: wie weit die Gewerke sind und was noch fehlt.
 */

/** Der Auftragsordner selbst. Wörtlich der Wert aus `regeln/ordner.ts`. */
const WURZEL = '(Auftragsordner)';

/* `bytes` STEHT IN `teile/dateiart.ts`. Es wird ab jetzt auch an der Kachel
   im Postfach und in der Behördenpost gebraucht, und eine zweite Fassung
   liefe beim ersten Nachtrag auseinander. */

/** Welcher Bereich gehört diesem Anmelder? `null` = alle (Innendienst). */
export function meinBereich(istSub: boolean, gewerke: any[]): string | null {
  if (!istSub) return null;
  const meines = (gewerke ?? []).find((g: any) => g.eigenes) ?? (gewerke ?? [])[0];
  return meines?.art === 'TB' ? 'tiefbau' : 'montage';
}

/**
 * DER EIGENE BEREICH ZUERST - ABER DIE ANDEREN SIND NICHT WEG.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „auch hier, die montage hat nichts mit dem tiefbau zu tun und andersrum.
 *    es geht hier um seinen bereich, seine dokumente und unterlagen. aber bei
 *    den dateien braucht die montage zugriff auf alle dateien die zum auftrag
 *    gehören, wie zb die die auskundung, die tiefbaubilder, falls da was
 *    geklärt werden muss oder nachgesehen werden muss."
 *
 * HIER STAND EIN `filter`, UND DAS WAR EINE HALBE ANTWORT. „Seine Ordner
 * zuerst" ist richtig, „nur seine Ordner" ist es nicht: der Monteur kommt
 * zwei Wochen nach dem Tiefbauer, das Rohr liegt anders als geplant, und die
 * Fotos, die es zeigen, waren für ihn nicht da.
 *
 * ABLEGEN DARF ER WEITER NUR BEI SICH. Das entscheidet `o.fuellbar` vom
 * Server (`subDarfFuellen`), nicht diese Sortierung - ein Riegel in der
 * Anzeige ist eine Bitte.
 */
export function meineOrdner<T extends { bereich: string }>(
  ordner: T[] | undefined, bereich: string | null
): T[] {
  const alle = ordner ?? [];
  if (!bereich) return alle;
  const meins = (o: T) => (o.bereich === bereich ? 0 : 1);
  return [...alle].sort((a, b) => meins(a) - meins(b));
}


export default function Dateien({
  ich, auftragId, gewerke, neuLaden,
}: {
  ich: Anmelder; auftragId: string;
  gewerke?: any[]; neuLaden?: () => Promise<void>;
}) {
  const [d, setD] = useState<any>(null);
  const [arten, setArten] = useState<any[]>([]);
  const [papierkorb, setPapierkorb] = useState<any[]>([]);
  // Nach wie vielen Tagen der Papierkorb geleert wird. 0 heisst: nie.
  const [korbTage, setKorbTage] = useState(0);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /* WELCHER GEWERKEBEREICH GERADE DASTEHT. „alle" ist die Vorgabe: ein
     Bildschirm, der vorgefiltert aufgeht, verschweigt beim ersten Blick,
     dass es mehr gibt. */
  const [filter, setFilter] = useState('alle');
  const [laedt, setLaedt] = useState<string | null>(null);
  /* DER GRUND, AN DER KARTE, AN DER GETIPPT WURDE. Siehe `fotoHochladen`. */
  const [ortfehler, setOrtfehler] =
    useState<{ pfad: string; text: string } | null>(null);
  const [bearbeite, setBearbeite] = useState<any>(null);
  const [entferne, setEntferne] = useState<any>(null);
  /* EINE MELDUNG TRÄGT IHRE ART MIT, und das ist keine Schönheit.
     ==========================================================================
     HIER STAND `string[]` UND EIN KASTEN MIT DEM FESTEN TITEL „Nicht
     hochgeladen:". Darin landeten BEIDE Sorten: was scheiterte UND was der
     Server mit einem Bild gemacht hat („kleiner gerechnet", seit dem
     16.09.2026 auch „Ortsangabe aufgebrannt"). **Eine Meldung, die „nicht
     hochgeladen" sagt, während die Datei danebensteht, schickt den Monteur
     zurück an die Grube** - und beim zweiten Mal glaubt er dem Kasten gar
     nichts mehr.
     Aufgefallen ist es am Kameraknopf: dessen Erfolgsmeldung ist lang und
     stand in voller Länge unter einer roten Überschrift. */
  const [meldung, setMeldung] =
    useState<Array<{ text: string; gut?: boolean }>>([]);
  /* WELCHE DATEI GERADE AN DER MAUS HÄNGT (09.10.2026). Solange sie hängt,
     ist jede Ordnerkarte ein Ziel; beim Loslassen wird nur der Ordner am
     Bezug geändert, der Inhalt bleibt, wo er liegt. */
  const [zieht, setZieht] =
    useState<{ id: string; dateiname: string; ordner: string } | null>(null);
  /* Der Satz nach dem Verschieben. Nicht unter „Angekommen": da kam nichts an. */
  const [verschoben, setVerschoben] = useState<string | null>(null);

  const darfLaden = darf(ich, 'datei.hochladen');
  /* WER DEN FORTSCHRITT DER GEWERKE SEHEN DARF. Das war bis zum 26.08.2026
     ein eigener Reiter; das Recht bleibt und heisst jetzt: die Zeile „so weit
     sind die Gewerke" steht da. Ein Recht abzuschaffen, weil ein Bildschirm
     verschwindet, nimmt es allen, die es haben. */
  const darfStand = darf(ich, 'akte.dokumentation');

  async function laden() {
    try {
      setD(await api.dateien(auftragId));
      if (!ich.istSub) {
        api.dateiPapierkorb(auftragId).then((k) => {
          setPapierkorb(k.zeilen ?? []);
          setKorbTage(Number(k.tage ?? 0));
        }).catch(() => {});
      }
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useFrisch(laden);
  useEffect(() => {
    laden();
    api.dateiarten().then(setArten).catch(() => {});
  }, [auftragId]);

  async function tu(was: () => Promise<any>) {
    setLaeuft(true); setFehler(null);
    try { await was(); await laden(); return true; }
    catch (f) { setFehler(ausFehler(f)); return false; }
    finally { setLaeuft(false); }
  }

  /**
   * Eine Datei an ihrer Kachel abhängen.
   *
   * ORIGINALTEXT MARVIN (27.08.2026)
   *   „Außerdem brauche ich bei den drag and drop die Möglichkeit die Dateien
   *    im allgemeinen mit einem ,x' oben rechts auch löschen zu können."
   *
   * OHNE GRUND, UND DAS IST HIER RICHTIG. Der Weg über „weg" in der
   * Einzelheitenliste fragt nach einem Grund, weil er der Weg des
   * Innendienstes an einem fremden Nachweis ist. Das x an der Kachel ist der
   * Weg dessen, der GERADE etwas hochgeladen hat und den Fehlgriff sofort
   * sieht: ein Pflichtfeld dazwischen macht aus einem Handgriff einen Vorgang.
   *
   * ES KOSTET NICHTS AUSSER EINEM ZWEITEN KLICK. Entfernt wird nur der BEZUG,
   * der Inhalt bleibt liegen, und die Datei steht danach im Papierkorb
   * darunter, mit „zurückholen" daneben. Deshalb genügt die Rückfrage an der
   * Kachel selbst; ein Fenster über dem halben Bildschirm wäre für einen
   * umkehrbaren Handgriff zu viel.
   */
  async function wegDamit(x: any) {
    await tu(() => api.dateiEntfernen(x.id, ''));
  }

  /**
   * EINE DATEI IN EINEN ANDEREN ORDNER ZIEHEN.
   *
   * ORIGINALTEXT MARVIN (09.10.2026)
   *   „es kann sein dass die partner die bilder falsch sortieren, ich will
   *    die bilder im innendienst verschieben können ohne dass ich sie erst
   *    herunterladen muss um sie dann im richtigen ordner wieder hochladen
   *    zu müssen. am besten per drag&drop und die verschiebst sie dann im
   *    hintergrund im ordner."
   *
   * ES IST KEIN HOCHLADEN. Die Ablage liegt nach Inhalt, der Ordner steht am
   * Bezug - verschieben heisst, dieses eine Feld zu ändern (derselbe Weg wie
   * „ändern" in der Liste). Die Kachel wandert SOFORT in die andere Karte,
   * der Server zieht nach; geht es schief, kommt sie zurück und der Grund
   * steht oben.
   */
  async function verschieben(x: { id: string; ordner: string; dateiname: string }, ziel: string) {
    if (!x || x.ordner === ziel) return;
    setFehler(null); setVerschoben(null);
    setD((alt: any) => alt ? { ...alt, dateien: alt.dateien.map((b: any) =>
      b.id === x.id ? { ...b, ordner: ziel } : b) } : alt);
    try {
      await api.dateiAendern(x.id, { ordner: ziel });
      setVerschoben(`${x.dateiname} liegt jetzt in ${ziel}.`);
    } catch (f) {
      setFehler(ausFehler(f));
    }
    await laden();
    await neuLaden?.().catch?.(() => {});
  }

  /**
   * Hochladen in GENAU DIESEN Ordner, auch wenn ein ganzer Ordner darauf fällt.
   *
   * Der Zielordner steckt im Aufruf, nicht in einem Formularfeld: der Monteur
   * steht vor der Grube und trifft EINE Entscheidung statt drei.
   *
   * FÄLLT EIN ORDNER DARAUF, BLEIBT SEINE STRUKTUR ERHALTEN. „Graben 1/Maße"
   * auf „Tiefbau" abgeworfen wird zu „Tiefbau/Graben 1/Maße" und nicht zu
   * fünfzehn Bildern in einem Topf. Deshalb wird nach Unterordner gruppiert
   * und je Gruppe einmal gesendet.
   */
  async function hochladen(pfad: string, stuecke: Baumdatei[]) {
    if (!stuecke.length) {
      setMeldung([{ text:
                  'Aus dieser Ablage kam keine einzige Datei. Manche Browser '
                + 'geben abgelegte Ordner nicht heraus; der Knopf am Ordner '
                + 'nimmt sie sicher.' }]);
      return;
    }
    setLaedt(pfad); setFehler(null); setMeldung([]);
    const schlecht: string[] = [];
    const gut: string[] = [];

    // Nach ZIELORDNER gruppieren: der Pfad im Abwurf hängt unter dem Ordner,
    // auf den abgeworfen wurde.
    const nach = new Map<string, File[]>();
    for (const s of stuecke) {
      const teile = s.pfad.replace(/\\/g, '/').split('/').filter(Boolean);
      const unter = teile.slice(0, -1).join('/');
      const ziel = unter ? `${pfad}/${unter}` : pfad;
      if (!nach.has(ziel)) nach.set(ziel, []);
      nach.get(ziel)!.push(await verkleinern(s.datei));
    }

    for (const [ziel, dateien] of nach) {
      /* EINE NACH DER ANDEREN. Acht Fotos brauchen leicht eine halbe Minute,
         und was dabei scheitert, muss einzeln benannt werden können. Im
         Bestand wurde eine unlesbare Datei still verworfen - der Ordner blieb
         leer, und der Monteur wurde ohne erkennbaren Grund am Absenden
         gehindert. */
      for (const datei of dateien) {
        try {
          /* KEINE ART. Bis zum 26.08.2026 stand hier fest „FOTO", und damit
             wurde jede PDF in einem Bilderordner zu einem Foto. Der Server
             bestimmt sie jetzt je Datei aus Name, Ordner und Dateityp. */
          const e = await api.dateienHochladen(auftragId, [datei], ziel,
                                               undefined, undefined);
          /* WAS DER SERVER MIT EINEM BILD GEMACHT HAT, STEHT DABEI. Eine
             Datei, die kleiner ankommt als sie losging, soll das sagen -
             sonst sucht jemand die „fehlenden" Megabyte. */
          for (const a of (e?.angekommen ?? []) as any[]) {
            if (a.verkleinert) gut.push(`${a.dateiname}: ${a.verkleinert}`);
          }
        } catch (x: any) {
          /* EIN ABGEWIESENES FORMAT SAGT, WAS STATT DESSEN GEHT. Der Hinweis
             kommt vom Server (415) und gehört in die Zeile, nicht in ein
             zweites Fenster. */
          schlecht.push(`${datei.name}: ${x?.message ?? 'ging nicht'}`
                        + (x?.hinweis ? ` ${x.hinweis}` : ''));
        }
      }
    }
    if (schlecht.length || gut.length) {
      setMeldung([...schlecht.map((t) => ({ text: t })),
                  ...gut.map((t) => ({ text: t, gut: true }))]);
    }
    await laden();
    await neuLaden?.();
    setLaedt(null);
  }

  /**
   * EIN FOTO AUS DEM KAMERAKNOPF, MIT SEINEN ORTSFELDERN.
   *
   * ORIGINALTEXT MARVIN (15.09.2026)
   *   „ein Klick im richten Ordner, die Kamera öffnet sich, Foto aufnehmen
   *    und es kommt direkt in den richtige Ordner."
   *
   * ES IST EIN EIGENER WEG UND NICHT `hochladen` MIT EINEM ZUSATZ. Der
   * Unterschied ist nicht der Parameter, sondern die Zahl: `hochladen`
   * gruppiert einen ganzen Ordnerbaum nach Unterordnern und sendet je Gruppe.
   * Hier gibt es genau EINE Datei und genau EINEN Ordner - der, an dessen
   * Knopf getippt wurde. Und sie geht NICHT durch `verkleinern` im Browser:
   * **wer ein Bild vor dem Stempeln zweimal neu speichert, verliert 2
   * Generationen an einem Nachweisfoto**; der Server verkleinert es ohnehin,
   * und der Stempel kommt danach darauf.
   */
  async function fotoHochladen(pfad: string, datei: File,
                               ort: Record<string, string>) {
    setLaedt(pfad); setFehler(null); setMeldung([]); setOrtfehler(null);
    /* `try/finally` UM `setLaedt(null)`.
       ORIGINALTEXT MARVIN (18.09.2026)
         „du drückst auf den knopf, ... aber dann wird es verworfen, es
          passiert nichts, es wird nicht gespeichert."
       Hier stand kein `finally`: warf `neuLaden?.()` (und das tut es, sobald
       die Verbindung wegbricht), lief `setLaedt(null)` nie. Der Knopf blieb
       für immer auf „Lädt hoch" und abgeblendet - und das sieht genauso aus
       wie „es passiert nichts". */
    try {
      const e = await api.dateienHochladen(auftragId, [datei], pfad,
                                           undefined, undefined, ort);
      const meldungen: string[] = [];
      for (const a of (e?.angekommen ?? []) as any[]) {
        if (a.verkleinert) meldungen.push(`${a.dateiname}: ${a.verkleinert}`);
      }
      if (meldungen.length) setMeldung(meldungen.map((t) => ({ text: t, gut: true })));
    } catch (x: any) {
      /* DER GRUND STEHT AM ORT DES KNOPFES UND NICHT OBEN AUF DER SEITE.
         Der Meldungskasten sitzt ganz oben; getippt wird der Knopf im Fuss
         einer Ordnerkarte weit unten. Auf 375 Punkten ist die Meldung damit
         ausserhalb des Bildes, und aus Sicht des Monteurs ist „keine Meldung"
         dasselbe wie „nichts passiert". Sie steht deshalb ZWEIMAL: oben für
         den, der hinsieht, und an der Karte für den, der gerade tippt. */
      const text = `${datei.name}: ${x?.message ?? 'ging nicht'}`
                 + (x?.hinweis ? ` ${x.hinweis}` : '');
      setMeldung([{ text }]);
      setOrtfehler({ pfad, text });
    } finally {
      await laden().catch(() => {});
      await neuLaden?.().catch?.(() => {});
      setLaedt(null);
    }
  }

  if (!d) return <div><Fehlerkasten lage={fehler} /></div>;

  const bereich = meinBereich(ich.istSub, gewerke ?? []);
  /* DER FILTER NACH GEWERK.
   *
   * ORIGINALTEXT MARVIN (01.09.2026)
   *   „bei den dateien vielleicht noch ein filter für die ordner der gewerke
   *    zb anzeigen zu lassen."
   *
   * SECHZEHN ORDNER SIND EINE SEITE ZUM ROLLEN. Wer nachsehen will, wie der
   * Graben aussah, hat drei Montageordner und den Papierkorb dazwischen.
   *
   * ER FILTERT DIE ANZEIGE UND NICHT DIE DATEN: die Zahl im Kopf sagt
   * weiterhin, wie viele Dateien der Auftrag hat, und der Knopf sagt, wie
   * viele Ordner er gerade zeigt. „Ein Deckel, der nichts sagt, wenn er
   * greift, ist eine Falle" - hier sagt er es doppelt.
   *
   * UND ES IST KEINE ZWEITE SICHTBARKEITSGRENZE. Was ein Sub überhaupt sieht,
   * entscheidet weiter der Server; dieser Knopf entscheidet nur, was von dem
   * Sichtbaren gerade dasteht. */
  /* DIE BEREICHE KOMMEN AUS `regeln/ordner.ts` und werden hier nicht neu
     erfunden: `wurzel`, `tiefbau`, `montage`, `umgebung`, `abrechnung`. Wer
     sie hier abschriebe, hätte beim nächsten Nachtrag einen Knopf, der immer
     null trifft. */
  const BEREICHE: { wert: string; text: string }[] = [
    { wert: 'alle', text: 'Alle Ordner' },
    { wert: 'wurzel', text: 'Auftragsordner' },
    { wert: 'tiefbau', text: 'Tiefbau' },
    { wert: 'montage', text: 'Montage' },
    { wert: 'umgebung', text: 'Umgebung' },
    { wert: 'abrechnung', text: 'Abrechnung' },
  ];
  const nachBereich = <T extends { bereich: string }>(o: T[]) =>
    filter === 'alle' ? o : o.filter((x) => x.bereich === filter);

  const strukturAlle = meineOrdner<any>(d.struktur ?? [], bereich);
  const weitereAlle = meineOrdner<any>(d.weitere ?? [], bereich);
  const struktur = nachBereich<any>(strukturAlle);
  const weitere = nachBereich<any>(weitereAlle);
  /* WELCHE KNÖPFE ÜBERHAUPT ETWAS TREFFEN. Ein Filter, der immer null trifft,
     ist kein Filter, sondern ein Knopf, der nichts tut - und auf 375 Pixeln
     nimmt er den Platz der anderen. Dieselbe Lehre wie beim Filter „neu" in
     der Auftragsliste. */
  const bereichZaehlt = (w: string) =>
    w === 'alle' ? strukturAlle.length + weitereAlle.length
    : [...strukturAlle, ...weitereAlle].filter((x: any) => x.bereich === w).length;

  /* WIE WEIT DIE GEWERKE SIND. Die Zahl steht EINMAL oben statt fünfzehnmal
     als roter Merker daneben. Gezählt werden nur die Ordner, die wirklich
     Pflicht sind; ein freiwilliger leerer Ordner ist kein Mangel. */
  /* GEZÄHLT WIRD NUR, WAS ER SELBST ABLEGEN KANN. Sonst steht in der
     Aufgabenliste des Monteurs „Tiefbau/03 NVt" - eine Aufgabe, die er nie
     erledigen kann, weil der Ordner für ihn gar keine Abwurffläche hat.
     „Eine Aufgabenliste mit fremden Aufgaben darin ist keine
     Aufgabenliste." */
  const pflichtig = (d.struktur ?? [])
    .filter((o: any) => o.pflicht && (o.fuellbar !== false));
  const offen = pflichtig.filter((o: any) => !o.anzahl);

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between',
                                      marginBottom: 12 }}>
        <div>
          <b>Dateien</b>{' '}
          <span className="klein leise">
            {d.dateien.length} in {d.ordner.length}{' '}
            {d.ordner.length === 1 ? 'Ordner' : 'Ordnern'}
          </span>
        </div>
        {/* DIE KNÖPFE STEHEN NUR DA, WENN ES ETWAS ZU FILTERN GIBT. Bei drei
            Ordnern ist eine Filterleiste mehr Bedienung als Inhalt. */}
        {/* NEBENEINANDER, NICHT UNTEREINANDER.
            ORIGINALTEXT MARVIN (02.09.2026)
              „hier ist oben die sortierung, welche ordner es gibt und wieviel
               bilder drin nicht richtig formatiert. mach das nebeneinander
               und achte darauf, dass die zahl einen abstand hat."
            Hier stand `knopfleiste breit` - ein RASTER. In dieser schmalen
            Flexspalte hatte es genau eine Spalte, und die sechs Knöpfe
            standen als Turm am rechten Rand. */}
        {bereichZaehlt('alle') > 4 && (
          <div className="filterleiste">
            {BEREICHE.filter((b) => bereichZaehlt(b.wert) > 0).map((b) => (
              <button key={b.wert} className="knopf klein"
                      aria-current={filter === b.wert}
                      onClick={() => setFilter(b.wert)}>
                {b.text}
                <span className="reiterzahl">{bereichZaehlt(b.wert)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* EIN FILTER, DER GREIFT, SAGT ES. Sonst sieht ein gefilterter
          Bildschirm aus wie ein Auftrag, an dem nichts liegt. */}
      {filter !== 'alle' && (
        <div className="hinweis" style={{ marginBottom: 12 }}>
          Es stehen nur die Ordner aus{' '}
          <b>{BEREICHE.find((b) => b.wert === filter)?.text}</b> da.
          {' '}
          <button className="knopf klein" onClick={() => setFilter('alle')}>
            Alle Ordner anzeigen
          </button>
        </div>
      )}

      <Fehlerkasten lage={fehler} />
      {verschoben && !zieht && (
        <div className="hinweis">{verschoben}</div>
      )}
      {zieht && (
        <div className="hinweis ziehhinweis">
          <b>{zieht.dateiname}</b> auf den Ordner ziehen, in den sie gehört.
          Der Inhalt bleibt, nur der Ordner ändert sich.
        </div>
      )}
      {/* ZWEI KÄSTEN UND NICHT EINER MIT EINEM FESTEN TITEL. Was scheiterte,
          steht rot; was der Server mit einer angekommenen Datei gemacht hat,
          steht daneben und heisst, was es ist. */}
      {meldung.some((m) => !m.gut) && (
        <div className="hinweis fehler">
          <b>Nicht hochgeladen:</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {meldung.filter((m) => !m.gut).slice(0, 6)
              .map((m, i) => <li key={i}>{m.text}</li>)}
          </ul>
        </div>
      )}
      {meldung.some((m) => m.gut) && (
        <div className="hinweis">
          <b>Angekommen:</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {meldung.filter((m) => m.gut).slice(0, 6)
              .map((m, i) => <li key={i}>{m.text}</li>)}
          </ul>
        </div>
      )}

      {/* WO IST DIE DATENBANK LEER, UND WO NUR DIESER AUFTRAG.
          Ein Auftrag ohne Datei ist normal. NULL Dateien im ganzen System sind
          etwas anderes, und beides stand einmal unter demselben Satz. */}
      {!d.dateien.length && d.dateienImSystem === 0 && !ich.istSub && (
        <div className="hinweis warnung" style={{ marginBottom: 14 }}>
          <b>In diesem System liegt noch keine einzige Datei.</b>
          Das ist keine Aussage über diesen Auftrag. Legen Sie die erste über
          eine der Flächen unten ab.
        </div>
      )}

      {/* SO WEIT SIND DIE GEWERKE. Eine Zeile statt fünfzehn roter Merker. */}
      {darfStand && pflichtig.length > 0 && (
        <div className={'hinweis' + (offen.length ? ' warnung' : '')}
             style={{ marginBottom: 14 }}>
          <b>
            {pflichtig.length - offen.length} von {pflichtig.length}{' '}
            Nachweisordnern gefüllt
          </b>
          {offen.length === 0
            ? 'Die Gewerke haben alles abgelegt, was für die Fertigmeldung '
              + 'gebraucht wird.'
            : `Es fehlt noch etwas in: ${offen.map((o: any) => o.pfad).join(', ')}. `
              + 'Das laden die Gewerke hoch, nicht der Innendienst.'}
        </div>
      )}

      {/* EIN FELD FÜR DIE WURZEL, und danach eines je Ordner.
          Marvin, 26.08.2026: „wenn ich dateien hochladen will, dann überall
          per drag and drop, nichts anderes." Hier stand ein Knopf, der ein
          Formular öffnete: Pfad tippen, Art wählen, Datei suchen, senden.
          Vier Entscheidungen für eine Handbewegung. Wer eine Datei in den
          falschen Ordner legt, zieht sie nicht noch einmal hoch - er ändert
          sie an Ort und Stelle. */}
      {darfLaden && !ich.istSub && (
        <Abwurf
          titel="Bescheide, Bauakte, Lagepläne hierher"
          klein
          laeuft={laedt === WURZEL}
          laufText="Lädt in den Auftragsordner"
          hinweis={'Sie landen im Auftragsordner, der Wurzel. Alles, was zu '
                 + 'einem Gewerk gehört, gehört auf dessen Ordner darunter. '
                 + 'Die Art bestimmt der Server.'}
          nimm={(x) => hochladen(WURZEL, x)} />
      )}

      {/* UND DER KNOPF, DER DIE KAMERA ÖFFNET. Er steht NEBEN der Fläche und
          nicht an ihrer Stelle: die Fläche nimmt eine Datei, die es schon
          gibt, der Knopf lässt eine ENTSTEHEN. Ein `capture` an der einen
          Fläche wäre kein zweiter Weg, sondern der Verlust des ersten -
          auf einem Telefon öffnete sie dann nur noch die Kamera, und der
          Lageplan aus der Galerie wäre nicht mehr erreichbar. */}
      {/* UND ZWAR AUCH FÜR DEN PARTNER. Hier stand `!ich.istSub` - also
          ausgerechnet nicht für den Monteur auf der Baustelle, der der
          Hauptnutzer dieses Knopfes ist. Was er in diesen Ordner legen darf,
          entscheidet der Server (`subDarfFuellen`), und der entscheidet es
          für jeden Ordner gleich. */}
      {darfLaden && (
        <Kameraknopf ordner={WURZEL} laeuft={laedt === WURZEL}
                     nimm={(f, ort) => fotoHochladen(WURZEL, f, ort)} />
      )}
      {ortfehler?.pfad === WURZEL && (
        <div className="hinweis warnung">{ortfehler.text}</div>
      )}

      {struktur.map((o: any) => (
        <Ordnerkarte
          key={o.pfad} o={o} d={d} ich={ich} darfLaden={darfLaden}
          laedt={laedt === o.pfad}
          hochladen={(x: Baumdatei[]) => hochladen(o.pfad, x)}
          fotoHochladen={(f: File, ort: Record<string, string>) =>
            fotoHochladen(o.pfad, f, ort)}
          fotofehler={ortfehler?.pfad === o.pfad ? ortfehler.text : null}
          setBearbeite={setBearbeite} setEntferne={setEntferne}
          wegDamit={wegDamit}
          zieht={zieht} setZieht={setZieht} verschieben={verschieben} />
      ))}

      {/* WAS NICHT IN DER STRUKTUR STEHT, VERSCHWINDET TROTZDEM NICHT.
          Gräben, Gruben und Nachweisordner entstehen zur Laufzeit, und aus
          dem Umzug kommen Ordner mit, die es in der Struktur nie gab. */}
      {weitere.length > 0 && (
        <>
          <h4 style={{ margin: '22px 0 6px' }}>
            Weitere Ordner{' '}
            <span className="klein leise">
              entstanden zur Laufzeit oder kamen aus dem Umzug mit
            </span>
          </h4>
          {weitere.map((o: any) => (
            <Ordnerkarte
              key={o.pfad} o={o} d={d} ich={ich} darfLaden={darfLaden}
              laedt={laedt === o.pfad}
              hochladen={(x: Baumdatei[]) => hochladen(o.pfad, x)}
              setBearbeite={setBearbeite} setEntferne={setEntferne}
              wegDamit={wegDamit}
              zieht={zieht} setZieht={setZieht} verschieben={verschieben} />
          ))}
        </>
      )}

      {/* DER PAPIERKORB, UND WIE LANGE ER HÄLT.
          Marvin, 27.08.2026: „wo ist der papierkorb und wie lange bleiben die
          dateien da drin bevor sie automatisch gelöscht werden?"

          ER STAND SCHON HIER, hiess aber nur „N entfernte Dateien" - und auf
          die zweite Frage gab es gar keine Antwort, weil nie etwas gelöscht
          wurde. „Bleibt für immer" ist eine vertretbare Regel; sie muss nur
          DASTEHEN, sonst rechnet jeder mit etwas anderem. */}
      {papierkorb.length > 0 && !ich.istSub && (
        <details className="papierkorb" style={{ marginTop: 20 }}>
          <summary className="klein">
            <b>Papierkorb</b>: {papierkorb.length}{' '}
            {papierkorb.length === 1 ? 'entfernte Datei' : 'entfernte Dateien'}
            {' '}
            <span className="leise">
              {korbTage > 0
                ? `- endgültig weg nach ${korbTage} Tagen`
                : '- sie bleiben, bis jemand sie endgültig entfernt'}
            </span>
          </summary>
          <p className="klein leise" style={{ margin: '6px 0' }}>
            {korbTage > 0
              ? `Ein täglicher Lauf entfernt hier endgültig, was länger als `
                + `${korbTage} Tage abgehängt ist. Bis dahin liegt der Inhalt `
                + 'noch da und lässt sich zurückholen.'
              : 'Hier wird nichts automatisch gelöscht. Der Inhalt liegt noch '
                + 'da und lässt sich jederzeit zurückholen. Wer eine Frist '
                + 'will, trägt sie in der Konfiguration unter '
                + '„papierkorb.tage" ein.'}
          </p>
          <div className="rollt">
            <table className="tabelle" style={{ marginTop: 6 }}>
              <tbody>
                {papierkorb.map((x) => (
                  <tr key={x.id}>
                    <td>{x.ordner}/{x.dateiname}</td>
                    <td className="klein leise">
                      entfernt {zeitpunkt(x.entfernt_am)}
                    </td>
                    <td style={{ width: 120 }}>
                      <button className="knopf klein" disabled={laeuft}
                              onClick={() => tu(() => api.dateiZurueckholen(x.id))}>
                        zurückholen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {bearbeite && (
        <DateiAendern
          x={bearbeite} arten={arten} istSub={ich.istSub}
          geschwister={d.geschwister ?? []}
          ordner={d.ordner.map((o: any) => o.name)}
          laeuft={laeuft} fehler={fehler} zu={() => setBearbeite(null)}
          speichere={async (b) => {
            if (await tu(() => api.dateiAendern(bearbeite.id, b))) setBearbeite(null);
          }}
        />
      )}

      {entferne && (
        <MitGrund
          titel={`"${entferne.dateiname}" entfernen`} knopf="Entfernen"
          erklaerung="Die Datei verschwindet aus der Ansicht. Der Inhalt bleibt
                      liegen und lässt sich zurückholen."
          laeuft={laeuft} fehler={fehler} zu={() => setEntferne(null)}
          fertig={async (g) => {
            if (await tu(() => api.dateiEntfernen(entferne.id, g))) setEntferne(null);
          }}
        />
      )}
    </div>
  );
}


/* DAS BLATT FÜR EINE DATEI, DIE KEIN BILD IST, STEHT NICHT MEHR HIER.
 *
 * Es stand ZWEIMAL im Haus: hier mit gezeichnetem Blatt, in der Auftragsakte
 * (Umgebungsbilder) ohne - und dort steht eine PDF deshalb als kaputtes
 * Bildzeichen da. Der Kommentar über der zweiten Fassung sagte selbst, dass
 * zwei Fassungen auseinanderlaufen; **das war eine Ankündigung und keine
 * Begründung.**
 *
 * Die Regeln liegen jetzt in `teile/dateiart.ts` (ohne JSX, damit ein
 * Prüfstand sie LADEN statt suchen kann), die Kachel in
 * `teile/Dateikachel.tsx`, das Fenster in `teile/Dateifenster.tsx`.
 */

/**
 * Ein Ordner der Struktur, mit dem, was darin liegt.
 *
 * ER IST SELBST DAS ABWURFFELD. „so ist das zu statisch" - der Weg war: Knopf
 * oben, Fenster auf, Pfad tippen, Art wählen, Datei suchen, senden. Jetzt
 * fällt das Bild auf den Ordner, in den es gehört, und der Rest ergibt sich
 * daraus.
 *
 * ER WIRD AUCH GEZEIGT, WENN ER LEER IST. Ein leerer Ordner beantwortet zwei
 * Fragen, die eine leere Seite nicht beantwortet: wohin gehört das, was
 * gleich hochgeladen wird, und fehlt hier etwas.
 *
 * „PFLICHT" STEHT NUR FÜR DEN, DER SIE HAT. Der Innendienst lädt diese
 * Nachweise nicht hoch; für ihn wäre der Merker eine Aufforderung an
 * jemanden, der nicht am Bildschirm sitzt.
 */
/** Die Kennzeichnung einer Kachel im Ziehen: so weiss die Karte, dass eine
    DATEI DES HAUSES kommt und kein Bild vom Schreibtisch. */
const ZIEHART = 'application/x-mds-dateibezug';

function Ordnerkarte({ o, d, ich, darfLaden, laedt, hochladen, fotoHochladen,
                       fotofehler, setBearbeite, setEntferne, wegDamit,
                       zieht, setZieht, verschieben }: any) {
  const [drueber, setDrueber] = useState(false);
  /* VERSCHIEBEN DARF NUR DER INNENDIENST (09.10.2026): „ich will die bilder
     im innendienst verschieben können". Der Partner legt ab, er sortiert
     nicht um - sonst wandern Nachweise aus einem Pflichtordner heraus, und
     der Ordner sieht aus wie nie gefüllt. */
  const darfSchieben = darfLaden && !ich.istSub;
  const istZiel = !!zieht && darfSchieben && zieht.ordner !== o.pfad;
  // Welche Kachel gerade nachfragt. Eine je Karte: zwei offene Rückfragen
  // nebeneinander wären zwei Fragen ohne erkennbare Antwort.
  const [fragt, setFragt] = useState<string | null>(null);
  /* DER HAKEN STEHT AM ANFANG UND ERREICHT JEDEN DURCHGANG. Ein Haken hinter
     einer bedingten Rückgabe ist auf dem Telefon ein weisser Bildschirm. */
  const { fenster, zeige } = useDateifenster();
  const drin = d.dateien.filter((x: any) => x.ordner === o.pfad);
  const hat = drin.length > 0;
  /* SEHEN JA, ABLEGEN NUR BEI SICH (31.08.2026). `fuellbar` kommt vom Server
     und ist für einen Monteur bei „Tiefbau/..." falsch. Eine Abwurffläche,
     die beim Loslassen mit 403 antwortet, ist ein Weg, der keiner ist. */
  const fuellbar = darfLaden && o.fuellbar !== false;
  // Der farbige Punkt sagt den Zustand. Nur für den, der ihn ändern kann:
  // beim Innendienst ist „leer" kein Mangel, den er beheben könnte.
  const klasse = hat ? 'hat'
    : (ich.istSub && o.pflicht && fuellbar) ? 'leer' : 'frei';

  return (
    <div
      className={'ordnerkarte ' + klasse + (drueber ? ' drueber' : '')
                 + (istZiel ? ' ziel' : '')}
      /* ZWEI DINGE KÖNNEN AUF DIE KARTE FALLEN: eine Datei vom Schreibtisch
         (hochladen, nur wo `fuellbar`) oder eine Kachel aus einem anderen
         Ordner (verschieben, nur Innendienst). Die Kachel trägt ZIEHART
         mit; daran wird unterschieden, nicht an der Maus. */
      onDragOver={(fuellbar || istZiel) ? (e) => {
        const kachel = e.dataTransfer.types.includes(ZIEHART);
        if (kachel ? !istZiel : !fuellbar) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = kachel ? 'move' : 'copy';
        setDrueber(true);
      } : undefined}
      onDragLeave={() => setDrueber(false)}
      onDrop={(fuellbar || istZiel) ? async (e) => {
        e.preventDefault(); setDrueber(false);
        if (e.dataTransfer.types.includes(ZIEHART)) {
          const x = zieht ?? JSON.parse(e.dataTransfer.getData(ZIEHART) || 'null');
          setZieht(null);
          if (x && istZiel) verschieben(x, o.pfad);
          return;
        }
        if (fuellbar) hochladen(await ausAbwurf(e.dataTransfer));
      } : undefined}
    >
      <div className="reihe kopfzeile">
        <span className="punkt" />
        <b className="wachsen">{o.pfad}</b>
        <span className="klein leise">
          {hat ? `${drin.length} ${drin.length === 1 ? 'Datei' : 'Dateien'}`
               + (o.bytes ? `, ${bytes(o.bytes)}` : '')
               : 'leer'}
        </span>
        {/* NUR FÜR DEN, DER IHN FÜLLEN MUSS. */}
        {ich.istSub && o.pflicht && !hat && fuellbar && (
          <span className="merker warnung">Pflicht</span>
        )}
        {/* UND WER NUR HINEINSIEHT, SOLL WISSEN, DASS ER NUR HINEINSIEHT.
            Ohne diesen Merker sieht ein fremder Ordner aus wie ein eigener,
            in dem gerade nichts hochgeladen werden kann. */}
        {ich.istSub && !fuellbar && (
          <span className="merker">nur ansehen</span>
        )}
      </div>

      {o.zweck && !hat && <div className="klein leise zweck">{o.zweck}</div>}
      {/* KEIN „freiwillig: freiwillig". `frei` gibt bei einem optionalen
          Ordner das Wort selbst zurück, und darunter stünde es dann zweimal. */}
      {ich.istSub && !o.pflicht && o.frei && (
        <div className="klein frei-tag">
          freiwillig{o.frei !== 'freiwillig' ? `: ${o.frei}` : ''}
        </div>
      )}

      {/* ALLE DATEIEN, JEDE MIT IHREM NAMEN.
          Marvin, 26.08.2026: „ich muss den dateinamen da stehen sehen. und
          verwende das richtige PDF symbol" / „zeige immer alle dateien an,
          nicht sowas wie 2 ... und 5 weitere. das geht nicht. ich muss immer
          alle dateien in allen ordnern sehen."

          HIER STAND EIN DECKEL: zehn Kacheln, dann eine Zahl. Fünfzehn
          Papiere im Auftragsordner sahen aus wie zehn gleiche graue Kästen
          mit „PDF" darin und einem Satz darunter, der sagte, dass es noch
          fünf gibt - aber nicht welche. Wer eine bestimmte Anzeige sucht,
          kommt so nie an sie heran; er muss erst die Liste aufklappen, die
          es nur für den Innendienst gibt. EINE ZAHL IST KEIN INHALT. */}
      {hat && (
        <div className="dateigitter">
          {drin.map((b: any) => (
            <Dateikachel key={b.id} datei={b}
                         oeffne={(x) => zeige(x, drin)}
                         zieh={darfSchieben ? {
                           laeuft: zieht?.id === b.id,
                           beginn: (e) => {
                             const x = { id: b.id, dateiname: b.dateiname, ordner: b.ordner };
                             e.dataTransfer.setData(ZIEHART, JSON.stringify(x));
                             e.dataTransfer.effectAllowed = 'move';
                             setZieht(x);
                           },
                           ende: () => setZieht(null),
                         } : undefined}
                         ecke={
                           /* DAS X OBEN RECHTS.
                              Es steht NEBEN dem Weg zur Datei und nicht
                              darin: ein Knopf in einem Knopf ist im Browser
                              nicht erlaubt, und ein Klick darauf würde
                              ausserdem die Datei öffnen. Es erscheint nur
                              für den, der hochladen darf - wer nicht ablegen
                              kann, soll auch nicht abhängen.

                              ES GEHT ALS `ecke` HINEIN UND NICHT MEHR ALS
                              KIND. Seit dem 08.09.2026 steht dort auch das
                              Zeichen zum Herunterladen; beide trügen sonst
                              ihre eigene absolute Lage und lägen übereinander
                              (Bild 2). */
                           darfLaden && fragt !== b.id ? (
                             <button className="kachelweg"
                                     title="Diese Datei entfernen"
                                     aria-label={`${b.dateiname} entfernen`}
                                     onClick={() => setFragt(b.id)}>×</button>
                           ) : null
                         }>
              {/* DIE RÜCKFRAGE LIEGT AUF DER KACHEL, nicht in einem Fenster.
                  Sie sagt auch, dass es umkehrbar ist: ohne diesen Satz
                  klickt niemand, der sich nicht ganz sicher ist. */}
              {fragt === b.id && (
                <div className="kachelfrage">
                  <span className="klein">Entfernen?</span>
                  <div className="reihe">
                    <button className="knopf klein warnung"
                            onClick={() => { setFragt(null); wegDamit(b); }}>
                      Ja
                    </button>
                    <button className="knopf klein"
                            onClick={() => setFragt(null)}>Nein</button>
                  </div>
                  <span className="klein leise">
                    Datei wird in den Papierkorb verschoben
                  </span>
                </div>
              )}

              {/* „intern" gehört an die Kachel, nicht nur in die Liste:
                  wer eine Datei anhängt, sieht sie hier - nicht dort. */}
              {!ich.istSub && !b.sichtbar_fuer_sub && (
                <span className="merker"
                      title="Der Subunternehmer sieht diese Datei nicht">
                  intern
                </span>
              )}
            </Dateikachel>
          ))}
        </div>
      )}

      {/* DAS FENSTER GEHÖRT DER KARTE UND NICHT DER SEITE.
          Wer eine Datei aufmacht, blättert durch die Dateien SEINES Ordners
          und nicht durch die des ganzen Auftrags: „nächste" heisst dann das,
          was daneben liegt. */}
      {fenster}

      {/* DIE LISTE MIT DEN EINZELHEITEN IST ZUGEKLAPPT, ABER SIE IST DA.
          Der Innendienst braucht Art, Herkunft, „intern" und die beiden
          Knöpfe; auf der Baustelle sind sie im Weg. Beides in einem
          Bildschirm heisst: das eine steht oben, das andere hinter einem
          Klick - und nicht in einem zweiten Reiter. */}
      {hat && !ich.istSub && (
        <details className="dateiliste">
          {/* Der NAME steht jetzt an der Kachel. Diese Klappe hiess bis
              eben „Namen, Art und Herkunft" - der erste Teil wäre ein
              Versprechen, das oben schon eingelöst ist. */}
          <summary className="klein leise">
            Art, Herkunft und Ändern
          </summary>
          {/* SIE ROLLT, STATT ABGESCHNITTEN ZU WERDEN.
              Fünf Spalten und zwei Knöpfe brauchen 621 Pixel; ein Telefon
              hat 375. Ohne diesen Kasten endete die Zeile am rechten Rand,
              und mit ihr die beiden Knöpfe „ändern" und „weg" - der
              Innendienst hätte am Telefon keine Datei mehr korrigieren
              können, ohne dass irgendetwas es sagt. Gefunden hat das erst
              Prüfstand 014, nachdem er diesen Reiter wieder ansieht. */}
          <div className="rollt">
          <table className="tabelle">
            <tbody>
              {drin.map((x: any) => (
                <tr key={x.id}>
                  <td style={{ width: '42%' }}>
                    <a href={api.dateiAdresse(x.id, x.dateiname)} target="_blank" rel="noreferrer">
                      {x.dateiname}
                    </a>
                    {!x.sichtbar_fuer_sub && (
                      <span className="merker"
                            title="Der Subunternehmer sieht diese Datei nicht">
                        intern
                      </span>
                    )}
                  </td>
                  <td className="klein">{x.art}</td>
                  <td className="klein">{bytes(Number(x.groesse))}</td>
                  <td className="klein leise">
                    {zeitpunkt(x.hochgeladen_am)}
                    {x.hochgeladen_von ? `, ${x.hochgeladen_von}` : ''}
                    {x.quelle === 'erzeugt' ? ', erzeugt' : ''}
                    {x.quelle === 'drive' ? ', aus dem Umzug' : ''}
                  </td>
                  <td style={{ width: 130 }}>
                    {darfLaden && (
                      <div className="reihe">
                        <button className="knopf klein"
                                onClick={() => setBearbeite(x)}>ändern</button>
                        <button className="knopf klein"
                                onClick={() => setEntferne(x)}>weg</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </details>
      )}

      {/* KEIN KNOPF MEHR, SONDERN EINE FLÄCHE.
          Marvin, 26.08.2026: „wenn ich dateien hochladen will, dann überall
          per drag and drop, nichts anderes." Hier standen ein Knopf
          „+ Dateien" und daneben der Satz „oder hierher ziehen" - zwei Wege
          für eine Handbewegung, und der eine als Fussnote des anderen.

          DIE FLÄCHE IST BEIDES: ziehen am Schreibtisch, tippen am Telefon.
          Ein Telefon kann kein Drag & Drop, und der Monteur vor der Grube
          ist der, der am meisten hochlädt - „nichts anderes" wörtlich
          genommen hätte ausgerechnet ihm den Weg genommen. */}
      {fuellbar && (
        <div className="ordnerfuss">
          <Abwurf
            titel={`In „${o.pfad}"`}
            klein laeuft={laedt}
            laufText="Lädt hoch"
            nimm={hochladen} />
          {/* DER KNOPF STEHT AN JEDEM ORDNER UND NICHT NUR AN DER WURZEL.
              „die Leute sehen von der Beschriftung das sie da Bilder machen
              müssen" - die Beschriftung steht AM ORDNER, also gehört der
              Knopf dorthin. Einer oben und eine Auswahlliste daneben wären
              wieder die 3 Entscheidungen, die dieses Haus 2026 abgeschafft
              hat. */}
          <Kameraknopf ordner={o.pfad} laeuft={laedt}
                       nimm={(f, ort) => fotoHochladen(f, ort)} />
        </div>
      )}
      {fotofehler && (
        <div className="hinweis warnung" style={{ margin: '0 8px 8px' }}>
          {fotofehler}
        </div>
      )}
    </div>
  );
}


/**
 * Dateien hochladen, mit Art und Sichtbarkeit.
 *
 * DER WEG FÜR DEN AUSNAHMEFALL, nicht mehr der Hauptweg. Bilder fallen auf
 * ihren Ordner; hier landet, was eine ANDERE Art braucht als „Foto" - ein
 * Bescheid, eine Rechnung, eine Bauakte.
 *
 * Der Haken wird dem Sub gar nicht erst gezeigt: er entscheidet die
 * Sichtbarkeit nicht, also ist die Frage für ihn keine. (Bis zum 26.08.2026
 * ging das Feld IMMER mit, auch beim Sub, und der Server weist genau dieses
 * Feld ab - jeder einzelne Upload eines Monteurs endete mit einem 403.)
 */
function DateiAendern({ x, arten, ordner, istSub, geschwister, zu, speichere, laeuft, fehler }: any) {
  const [name, setName] = useState(x.dateiname);
  /* ZUM GESCHWISTER LEGEN (.524). Marvin, 28.10.2026: die VRAO lag am
     Hauptauftrag, beantragt hatte die Mängelbeseitigung. Leer heisst: sie
     bleibt, wo sie ist. */
  const [auftrag, setAuftrag] = useState('');
  const [ord, setOrd] = useState(x.ordner);
  const [art, setArt] = useState(x.art);
  const [sichtbar, setSichtbar] = useState(x.sichtbar_fuer_sub);
  const nurIntern = arten.find((a: any) => a.wert === art)?.nur_intern;

  return (
    <Dialog titel="Datei ändern" laeuft={laeuft} zu={zu} breite={620}>
      <Fehlerkasten lage={fehler} />
      <Text name="dateiname" titel="Name" pflicht wert={name} setze={setName}
            breite="100%" fehler={fehler} />
      <div className="feldreihe">
        <Text name="ordner" titel="Ordner" pflicht wert={ord} setze={setOrd}
              breite={300} fehler={fehler} />
        {!istSub && (
          <Auswahl name="art" titel="Art" wert={art} setze={setArt} leerText=""
                   fehler={fehler}
                   optionen={arten.map((a: any) => ({ wert: a.wert, text: a.wert }))} />
        )}
      </div>
      {!istSub && geschwister?.length > 0 && (
        <Auswahl name="auftrag_id" titel="Zu einem anderen Auftrag dieses Hauses legen"
                 wert={auftrag} setze={setAuftrag} leerText="bleibt hier"
                 fehler={fehler}
                 optionen={geschwister.map((g: any) => ({ wert: g.id, text: g.text }))} />
      )}
      {!istSub && (
        <Haken name="sichtbar" titel="Der Subunternehmer darf sie sehen"
               wert={nurIntern ? false : sichtbar} setze={setSichtbar}
               gesperrt={nurIntern}
               hinweis={nurIntern ? `Eine ${art} bleibt intern.` : undefined} />
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={() => speichere({
                  dateiname: name, ordner: ord,
                  ...(istSub ? {} : { art, sichtbar_fuer_sub: nurIntern ? false : sichtbar }),
                  ...(auftrag ? { auftrag_id: auftrag } : {}),
                })}>
          Speichern
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}
